<template>
  <div>
    <div v-if="!items" class="loadingMessage">{{ $t('general.loading') }}</div>
    <v-table v-if="items" class="walletTable" density="comfortable">
      <thead>
        <tr>
          <th
            v-for="header in headers"
            :key="header.value"
            :class="[
              'column',
              header.value === pagination.sortBy ? (pagination.descending ? 'desc' : 'asc') : '',
              header.value === pagination.sortBy ? 'active' : '',
              header.sortable === false ? '' : 'sortable',
              header.value === 'updatedAt' ? 'updated-at-header' : ''
            ]"
            @click="header.sortable !== false && changeSort(header.value)"
          >
            <v-icon
              size="small"
              v-if="header.sortable !== false"
            >
              {{ header.value === pagination.sortBy && pagination.descending ? 'arrow_downward' : 'arrow_upward' }}
            </v-icon>
            <span class="table-header-label">{{ header.text }}</span>

            <v-tooltip v-if="header.tooltip" location="right">
              <template #activator="{ props }">
                <span v-bind="props">
                  <v-icon size="small" style="opacity: unset;">info</v-icon>
                </span>
              </template>
              <div v-html="header.tooltipText" style="padding: 15px;" />
            </v-tooltip>
          </th>
          <th v-if="editDelete" class="justify-center layout" />
        </tr>
      </thead>
      <tbody>
        <tr v-if="!paginatedItems || paginatedItems.length === 0">
          <td :colspan="headers ? headers.length + (editDelete ? 1 : 0) : 1" class="no-data-row">
            No data available
          </td>
        </tr>
        <tr v-for="row in paginatedItems" :key="row.publicId || row.name || row.email">
          <td v-for="header in headers" :key="(row.publicId || row.name || row.email) + header.value">
            <div v-if="header.value == 'dataActions'" :style="'padding-left: ' + (header.sortable === false ? 0 : 20) + 'px;'">
              <DataActions
                :item="row"
                :transferCallback="transferCallback"
                :deleteItemCallback="deleteCallback"
                :embedCallback="embedCallback"
                :postCallback="postCallback"
                :unpostCallback="unpostCallback"
                :borrowCallback="borrowCallback"
                :unborrowCallback="unborrowCallback"
                :dataProductCallback="dataProductCallback"
                :dataProductStatusChangedCallback="dataProductStatusChangedCallback"
                :buyDataProductCallback="buyDataProductCallback"
                :canTransfer="canTransfer(row)"
                :isCurrentUser="row.isCurrentUser"
                :page="page"
              />
            </div>
            <div v-else-if="row[header.value] != null" :style="'padding-left: ' + (header.sortable === false ? 0 : 20) + 'px;'">
              <a v-if="linkBuilder && linkBuilder(header.value, row)" :href="linkBuilder(header.value, row)" :target="linkTarget">
                <span v-if="checkData(row[header.value]) === true">{{ convertData(row[header.value]) }}</span>
                <span v-else-if="header.value == 'dataApps'">
                  <DataApps :item="row" :transferCallback="transferCallback" :canTransfer="canTransfer(row)" />
                </span>
                <span v-else>{{ row[header.value] }}</span>
              </a>
              <span v-else>
                <span v-if="checkData(row[header.value]) === true">{{ convertData(row[header.value]) }}</span>
                <span v-else-if="header.value == 'dimScoreTotal'">
                  <v-tooltip location="right">
                    <template #activator="{ props }">
                      <div v-bind="props" class="dimColumnText">
                        <span>{{ row[header.value] }}</span>
                      </div>
                    </template>
                    <span><b>D</b>: {{ row.dimScore.D.total.toFixed(2) }}</span> &nbsp;&nbsp;
                    <span><b>I</b>: {{ row.dimScore.I.total.toFixed(2) }}</span> &nbsp;
                    <span><b>M</b>: {{ row.dimScore.M.total.toFixed(2) }}</span>
                  </v-tooltip>
                </span>
                <span v-else-if="header.value == 'pii'">
                  <img :src="getPIIIcon(row)" class="piiIcon" />
                </span>
                <span v-else-if="header.value == 'dataApps'">
                  <DataApps :item="row" :transferCallback="transferCallback" :canTransfer="canTransfer(row)" />
                </span>
                <span v-else-if="header.value == 'wrangler'" :class="{ bolded: row.isCurrentUser }">
                  {{ row[header.value] }}
                </span>
                <span v-else-if="header.value == 'size' || header.value == 'numberOfCells' || header.value == 'numberOfDatasets'">
                  {{ abbreviateNumber(row[header.value]) }}
                </span>
                <span v-else>{{ row[header.value] }}</span>
              </span>
            </div>
            <div class="empty-field" :style="'padding-left: ' + (header.sortable === false ? 0 : 20) + 'px;'" v-else>
              -
            </div>
          </td>
          <td v-if="editDelete" class="justify-center layout">
            <v-icon
              v-if="row.updateAllowed"
              size="small"
              class="me-2"
              @click="editItem(row)"
            >
              edit
            </v-icon>
            <v-icon
              v-if="row.deleteAllowed"
              size="small"
              @click="deleteItem(row)"
            >
              delete
            </v-icon>
          </td>
        </tr>
      </tbody>
    </v-table>

    <div v-if="items" class="v-datatable__actions">
      <div class="v-datatable__actions__select">
        Rows per page:
        <select v-model.number="pagination.rowsPerPage" @change="currentPage = 1">
          <option v-for="n in rowsPerPageItems" :key="n" :value="n">{{ n }}</option>
        </select>
      </div>
      <div class="v-datatable__actions__range-controls">
        <span>{{ rangeText }}</span>
        <v-btn
          icon
          variant="text"
          class="v-datatable__actions__nav"
          :disabled="currentPage <= 1"
          aria-label="Previous page"
          @click="currentPage--"
        >
          <v-icon>chevron_left</v-icon>
        </v-btn>
        <v-btn
          icon
          variant="text"
          class="v-datatable__actions__nav"
          :disabled="currentPage >= pageCount"
          aria-label="Next page"
          @click="currentPage++"
        >
          <v-icon>chevron_right</v-icon>
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script>
import Utils from '../mixins/utils';
import DataActions from './DataActions.vue'
import DataApps from './DataApps.vue'
import moment from 'moment';

import YesIcon from '../assets/imgs/yes_icon.png';
import NoIcon from '../assets/imgs/no_icon.png';
import UnknownIcon from '../assets/imgs/unknown_icon.png';

export default {
  components: {
    DataActions,
    DataApps
  },
  mixins: [Utils],
  name: 'data-table',
  props: {
    defaultSortBy: {
      type: String,
      default: 'name'
    },
    sortDesc: Boolean,
    headers: {
      type: Array
    },
    items: {
      type: Array
    },
    editDelete: Boolean,
    namesToLinks: Boolean,
    page: String,
    linkBuilder: Function,
    linkTarget: String,
    editCallback: Function,
    transferCallback: Function,
    deleteCallback: Function,
    embedCallback: Function,
    postCallback: Function,
    unpostCallback: Function,
    borrowCallback: Function,
    unborrowCallback: Function,
    dataProductCallback: Function,
    dataProductStatusChangedCallback: Function,
    buyDataProductCallback: Function
  },
  data() {
    return {
      currentPage: 1,
      rowsPerPageItems: [10, 50, 100],
      pagination: {
        sortBy: this.defaultSortBy || 'name',
        descending: this.sortDesc,
        rowsPerPage: 100
      }
    }
  },
  computed: {
    rangeText() {
      if (!this.items || !this.items.length) {
        return '-';
      }
      const start = (this.currentPage - 1) * this.pagination.rowsPerPage + 1;
      const end = Math.min(this.currentPage * this.pagination.rowsPerPage, this.items.length);
      return `${start}-${end} of ${this.items.length}`;
    },
    sortedItems() {
      if (!this.items) {
        return [];
      }

      const items = [...this.items];
      const sortKey = this.pagination.sortBy;
      const descending = this.pagination.descending;

      return items.sort((a, b) => {
        const aVal = a[sortKey];
        const bVal = b[sortKey];
        if (aVal === bVal) return 0;
        if (aVal == null) return 1;
        if (bVal == null) return -1;
        if (aVal < bVal) return descending ? 1 : -1;
        if (aVal > bVal) return descending ? -1 : 1;
        return 0;
      });
    },
    paginatedItems() {
      const start = (this.currentPage - 1) * this.pagination.rowsPerPage;
      return this.sortedItems.slice(start, start + this.pagination.rowsPerPage);
    },
    pageCount() {
      if (!this.items) return 1;
      return Math.max(1, Math.ceil(this.items.length / this.pagination.rowsPerPage));
    }
  },
  watch: {
    items() {
      this.currentPage = 1;
    }
  },
  methods: {
    changeSort(column) {
      if (this.pagination.sortBy === column) {
        this.pagination.descending = !this.pagination.descending;
      } else {
        this.pagination.sortBy = column;
        this.pagination.descending = false;
      }
    },
    editItem(item) {
      this.editCallback(item);
    },
    deleteItem(item) {
      document.getElementById('elementToBeDeleted').dataset.publicId = item.publicId;
      document.getElementById('elementToBeDeleted').innerHTML = item.name ? item.name : item.email;
      document.getElementById('deleteModal').classList.remove('invisible');
    },
    checkData(date) {
      return /^[0-9]{4}-[0-9]{2}-[0-9]{2}T[0-9]{2}:[0-9]{2}:[0-9]{2}(.[0-9]+)?Z$/.test(date);
    },
    convertData(date) {
      const dateParse = new Date(date);
      return moment(dateParse).format('DD MMM YYYY');
    },
    canTransfer(item) {
      return (this.page == 'dwallet' || this.page == 'exchange')
        && (item.type == 'dataset' || item.type == 'dataproduct')
        && item.isCurrentUser;
    },
    getPIIIcon(item) {
      if (item.pii == 'yes') return YesIcon;
      if (item.pii == 'no') return NoIcon;
      return UnknownIcon;
    }
  }
}
</script>

<style>
  .no-data-row {
    text-align: center;
    color: rgba(0, 0, 0, 0.54);
    font-size: 14px;
    padding: 16px 0 !important;
    background-color: #f5f5f5;
  }
  .loadingMessage{
    text-align: center;
    font-weight: bold;
    color: #666;
    margin-top: 60px;
    margin-bottom: 100px;
  }
  .bolded{
    font-weight: bold;
  }
  .piiIcon{
    width: 16px;
  }
  .walletTable {
    width: 100%;
  }
  .walletTable.v-table > .v-table__wrapper > table {
    width: 100%;
    table-layout: auto;
  }
  .walletTable table th, .walletTable table td {
      padding: 0px 10px !important;
      font-size: 14px;
  }
  .walletTable table td a,
  .walletTable table td a span {
    color: #1976d2 !important;
    text-decoration: none;
  }
  .walletTable table td a:hover,
  .walletTable table td a:hover span {
    color: #1565c0 !important;
    text-decoration: none;
  }
  .walletTable.v-table > .v-table__wrapper > table > thead > tr > th.column,
  .walletTable.v-table > .v-table__wrapper > table > thead > tr > th.column .table-header-label {
    white-space: nowrap;
    color: rgba(0, 0, 0, 0.87) !important;
  }
  .walletTable.v-table > .v-table__wrapper > table > thead > tr > th.column.sortable {
    cursor: pointer;
  }
  .walletTable.v-table > .v-table__wrapper > table > thead > tr > th.column.active,
  .walletTable.v-table > .v-table__wrapper > table > thead > tr > th.column.active .table-header-label {
    color: #000 !important;
  }
  .walletTable.v-table > .v-table__wrapper > table > thead > tr > th.updated-at-header,
  .walletTable.v-table > .v-table__wrapper > table > thead > tr > th.updated-at-header .table-header-label {
    color: #000 !important;
  }
  .walletTable.v-table > .v-table__wrapper > table > thead > tr > th.column .v-icon {
    color: inherit !important;
    opacity: 0.38;
  }
  .walletTable.v-table > .v-table__wrapper > table > thead > tr > th.column.active .v-icon,
  .walletTable.v-table > .v-table__wrapper > table > thead > tr > th.updated-at-header .v-icon {
    opacity: 1;
  }
  .v-datatable__actions {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    font-size: 12px;
    flex-wrap: wrap-reverse;
    color: rgba(0, 0, 0, 0.54);
    border-top: 1px solid rgba(0, 0, 0, 0.12);
    padding: 0 4px;
    overflow: visible;
    width: 100%;
  }
  .v-datatable__actions__select {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    margin-right: 14px;
    white-space: nowrap;
  }
  .v-datatable__actions__select select {
    margin: 13px 0 13px 34px;
    border: none;
    background: transparent;
    color: rgba(0, 0, 0, 0.87);
    font-size: 12px;
  }
  .v-datatable__actions__range-controls {
    display: flex;
    align-items: center;
    min-height: 48px;
  }
  .v-datatable__actions__range-controls span {
    margin: 0 32px 0 24px;
  }
  .v-datatable__actions__nav {
    color: rgba(0, 0, 0, 0.54);
    margin-left: 14px;
    min-width: 34px;
    min-height: 34px;
  }
  .v-datatable__actions__nav .v-icon {
    font-size: 24px;
  }
  .v-datatable__actions__nav.v-btn--disabled {
    opacity: 0.38;
  }
  .dimColumnText{
    font-size: 16px;
    color: #EC008C;
    font-family: Roboto;
    font-weight: 400;
    width: min-content;
  }
  .v-tooltip > .v-overlay__content {
    background: #ffffff !important;
    color: #666 !important;
    max-width: 400px;
    opacity: 1 !important;
    -webkit-box-shadow: 2px 2px 14px -4px rgba(0,0,0,0.75);
    -moz-box-shadow: 2px 2px 14px -4px rgba(0,0,0,0.75);
    box-shadow: 2px 2px 14px -4px rgba(0,0,0,0.75);
  }
  .v-tooltip > .v-overlay__content::before {
    right: 100%;
    top: 50%;
    border: solid transparent;
    content: " ";
    height: 0;
    width: 0;
    position: absolute;
    pointer-events: none;
    border-color: rgba(255, 255, 255, 0);
    border-right-color: #ffffff;
    border-width: 5px;
    margin-top: -5px;
  }
</style>
